import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import type { UseCollectionResult } from '@hooks/dashboard/useCollection'
import { FinanceGrossNetLineChart } from '@components/pages/Dashboard/Finance/SectionCards/shared/FinanceGrossNetLineChart.component'
import { HYGIENE_PLACEHOLDER } from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import type { FinanceSectionCardBaseProps } from '@dataTypes/dashboard.types'
import {
  FinanceGrossNetLineLegend,
  FinanceMetricTilesRow,
} from '../../FinanceMetricTiles.component'

type CollectionCardProps = FinanceSectionCardBaseProps & {
  titleHref: string
  collection: UseCollectionResult
}

export function CollectionCard({
  titleHref,
  periodLabel,
  collection,
}: CollectionCardProps): React.ReactElement {
  const {
    loading,
    grossStr,
    netStr,
    rateStr,
    changeSignedPct,
    changeAmt,
    grossNetChart,
    goalLabel,
    goalValue,
    collGoalLine,
    hasPayload,
    hasGrossData,
    hasNetData,
    hasRateData,
    hasTrendData,
  } = collection
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasGrossData || hasNetData || hasRateData || hasTrendData,
  )
  const isChartEmpty = shouldShowDashboardSectionEmpty(loading, hasTrendData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  const changeLine = loading
    ? HYGIENE_PLACEHOLDER
    : `${changeSignedPct} (${changeAmt}) vs prior period`

  return (
    <DashboardSectionCard
      title="Collection"
      titleHref={titleHref}
      className="dashboard-finance-collection-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-finance-production">
        <div className="dashboard-finance-production-header">
          <span className="dashboard-finance-production-goal">{goalLabel}</span>
          <span className="dashboard-finance-production-change">
            {changeLine}
          </span>
        </div>
        <FinanceMetricTilesRow
          columns={4}
          metrics={[
            {
              id: 'gross-collection',
              leftLabel: 'Gross collection',
              value: loading ? HYGIENE_PLACEHOLDER : grossStr,
              rightLabel: periodLabel,
            },
            {
              id: 'net-collection',
              leftLabel: 'Net collection',
              value: loading ? HYGIENE_PLACEHOLDER : netStr,
              rightLabel: periodLabel,
            },
            {
              id: 'goal',
              leftLabel: 'Goal',
              value: goalValue,
              rightLabel: periodLabel,
            },
            {
              id: 'collection-rate',
              leftLabel: 'Collection rate',
              value: loading ? HYGIENE_PLACEHOLDER : rateStr,
              rightLabel: periodLabel,
            },
          ]}
        />
        {isChartEmpty ? (
          <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
        ) : (
          <div className="dashboard-finance-production-chart-wrap">
            <div className="dashboard-finance-production-chart">
              <FinanceGrossNetLineChart
                data={loading ? [] : grossNetChart.data}
                yDomain={grossNetChart.yDomain}
                yTicks={grossNetChart.yTicks}
                goalLineValue={collGoalLine}
              />
            </div>
            <FinanceGrossNetLineLegend showGoal />
          </div>
        )}
      </div>
    </DashboardSectionCard>
  )
}
